import {
  BackupPinError,
  type BackupStatus,
  type SignOutCheck,
  validPin,
  validRecoveryCode,
} from "@rhema/client";
import { Button, Card, SettingRow, Sheet, Switch, TextField } from "@rhema/ui";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useFeedback } from "../menus/Feedback";
import { useClient } from "../session/session";

// The encrypted key backup, as the person sees it: the backup PIN that restores messages on a new
// device when every other device is gone, the prompt to set one, the prompt to use it, and the
// warning before signing out would leave messages unreadable. Also recovery through Rhema support:
// the recovery code staff give a person who lost every device and forgot their PIN, and the
// advanced protection switch that turns that recovery off.

/** When the next PIN try is allowed, in words. */
export function tryAgainAt(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (at.toDateString() === now.toDateString()) return time;
  return `${at.toLocaleDateString([], { weekday: "long" })} at ${time}`;
}

/** What the sign-out sheet says when signing out would leave messages unreadable. */
export function signOutRisk(
  reason: Extract<SignOutCheck, { safe: false }>["reason"],
  where: "device" | "browser",
) {
  const why =
    reason === "not_backed_up"
      ? `Rhema could not save this ${where}'s message keys to your backup.`
      : `This is the only ${where} with the key to your message backup, and you have no backup PIN.`;
  return {
    title: `Messages on this ${where}`,
    detail: `${why} If you sign out and remove them, messages that only this ${where} can read will become unreadable. Keeping them signs you out and leaves them here, so they come back when you sign in again.`,
    other: `Keep this ${where}'s messages`,
    action: "Sign out anyway",
  };
}

type PinMode = "set" | "reset" | "change" | "recover";
type Mode = PinMode | "code";

const TITLES: Record<PinMode, string> = {
  set: "Set a backup PIN",
  reset: "Set a new backup PIN",
  change: "Change your backup PIN",
  recover: "Restore your messages",
};

const INTRO: Record<PinMode, string> = {
  set: "If you lose all your devices, your backup PIN restores your messages on a new one. If you also forget the PIN, Rhema support can help you recover them unless you turn on Advanced protection. Use 6 or more digits, and pick one you do not use to sign in if you can.",
  reset:
    "Your messages are restored. Set a new backup PIN so you can restore them yourself if this happens again. Use 6 or more digits.",
  change:
    "If you lose all your devices, your backup PIN restores your messages on a new one. Use 6 or more digits, and pick one you do not use to sign in if you can.",
  recover: "Enter your backup PIN to read the messages from your other devices.",
};

/** What the person reads when a recovery from Rhema support starts. */
export const RECOVERY_STARTED = "Rhema support started a recovery of your message history.";

/** Sets, changes or uses the backup PIN. */
export function PinSheet({
  mode,
  open,
  onClose,
  onSkip,
  onUseCode,
}: {
  mode: PinMode;
  open: boolean;
  onClose: (done: boolean) => void;
  /** Shown as "Not now". Without it the sheet offers Cancel. */
  onSkip?: () => void;
  /** When restoring, offers a recovery code from Rhema support instead of the PIN. */
  onUseCode?: () => void;
}) {
  const client = useClient();
  const { notify } = useFeedback();
  const [pin, setPin] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPin("");
    setAgain("");
    setError(null);
    setBusy(false);
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validPin(pin)) return setError("Use 6 or more digits.");
    if (mode !== "recover" && pin !== again) return setError("The two PINs do not match.");
    setBusy(true);
    setError(null);
    try {
      if (mode === "recover") {
        const result = await client.recoverWithPin(pin);
        if (!result.ok) {
          setBusy(false);
          if (result.reason === "none") return setError("No backup PIN is set for your account.");
          if (result.reason === "locked" && result.lockedUntil)
            return setError(`Too many wrong PINs. You can try again at ${tryAgainAt(result.lockedUntil)}.`);
          return setError(
            result.lockedUntil
              ? `That PIN is not right. You can try again at ${tryAgainAt(result.lockedUntil)}.`
              : "That PIN is not right. Check it and try again.",
          );
        }
        notify("Your messages are restored.");
      } else {
        await client.setBackupPin(pin);
        notify(mode === "change" ? "Your backup PIN is changed." : "Your backup PIN is set.");
      }
      onClose(true);
    } catch (err) {
      setBusy(false);
      setError(err instanceof BackupPinError ? err.message : "Rhema could not reach the server. Try again.");
    }
  };

  return (
    <Sheet open={open} onClose={() => onClose(false)} title={TITLES[mode]} className="px-5 pb-7">
      <p className="m-0 mt-3 text-[15px] leading-normal text-ink-secondary">{INTRO[mode]}</p>
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
        <TextField
          label="Backup PIN"
          type="password"
          inputMode="numeric"
          autoComplete={mode === "recover" ? "current-password" : "new-password"}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          autoFocus
        />
        {mode !== "recover" && (
          <TextField
            label="Enter it again"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={again}
            onChange={(e) => setAgain(e.target.value.replace(/\D/g, ""))}
          />
        )}
        {error && (
          <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" disabled={busy}>
          {busy ? (mode === "recover" ? "Restoring" : "Saving") : mode === "recover" ? "Restore" : "Set PIN"}
        </Button>
        {mode === "recover" && onUseCode && (
          <Button variant="ghost" disabled={busy} onClick={onUseCode}>
            Use a recovery code from Rhema support
          </Button>
        )}
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() => {
            onSkip?.();
            onClose(false);
          }}
        >
          {onSkip ? "Not now" : "Cancel"}
        </Button>
      </form>
    </Sheet>
  );
}

/** Uses a one-time recovery code from Rhema support, then asks for a new backup PIN. */
export function CodeSheet({
  open,
  onClose,
  onSkip,
}: {
  open: boolean;
  onClose: (done: boolean) => void;
  /** Shown as "Not now". Without it the sheet offers Cancel. */
  onSkip?: () => void;
}) {
  const client = useClient();
  const { notify } = useFeedback();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode("");
    setError(null);
    setBusy(false);
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validRecoveryCode(code)) return setError("A recovery code has 20 letters and numbers.");
    setBusy(true);
    setError(null);
    try {
      const result = await client.recoverWithCode(code);
      if (!result.ok) {
        setBusy(false);
        if (result.reason === "none")
          return setError(
            "There is no recovery code for your account, or it has expired. Ask Rhema support for a new one.",
          );
        if (result.reason === "invalid") return setError("A recovery code has 20 letters and numbers.");
        if (result.reason === "locked" && result.lockedUntil)
          return setError(`Too many wrong codes. You can try again at ${tryAgainAt(result.lockedUntil)}.`);
        return setError(
          result.lockedUntil
            ? `That code is not right. You can try again at ${tryAgainAt(result.lockedUntil)}.`
            : "That code is not right. Check it and try again.",
        );
      }
      notify("Your messages are restored.");
      onClose(true);
    } catch {
      setBusy(false);
      setError("Rhema could not reach the server. Try again.");
    }
  };

  return (
    <Sheet open={open} onClose={() => onClose(false)} title="Use a recovery code" className="px-5 pb-7">
      <p className="m-0 mt-3 text-[15px] leading-normal text-ink-secondary">
        Enter the recovery code Rhema support gave you after checking who you are. It works once, for 72
        hours.
      </p>
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
        <TextField
          label="Recovery code"
          autoComplete="one-time-code"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoFocus
        />
        {error && (
          <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" disabled={busy}>
          {busy ? "Restoring" : "Restore"}
        </Button>
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() => {
            onSkip?.();
            onClose(false);
          }}
        >
          {onSkip ? "Not now" : "Cancel"}
        </Button>
      </form>
    </Sheet>
  );
}

/** The PIN and code sheets, one at a time. After a recovery code, it asks for a new PIN. */
function BackupSheets({
  mode,
  setMode,
  onSkip,
  onDone,
}: {
  mode: Mode | null;
  setMode: (mode: Mode | null) => void;
  onSkip?: () => void;
  onDone?: (mode: Mode, done: boolean) => void;
}) {
  if (mode === "code")
    return (
      <CodeSheet
        open
        onSkip={onSkip}
        onClose={(done) => {
          onDone?.("code", done);
          setMode(done ? "reset" : null);
        }}
      />
    );
  return (
    <PinSheet
      mode={mode ?? "set"}
      open={mode !== null}
      onSkip={onSkip}
      onUseCode={() => setMode("code")}
      onClose={(done) => {
        if (mode) onDone?.(mode, done);
        setMode(null);
      }}
    />
  );
}

function statusLine(s: BackupStatus): string {
  if (s.waitingForKey)
    return s.pin
      ? "This device does not have your backup key yet. Enter your backup PIN to read your earlier messages."
      : "This device is waiting for your backup key from another of your devices.";
  if (s.pin) return "Your message keys are backed up, and your backup PIN can restore them on a new device.";
  return "Your message keys are backed up. Set a backup PIN so you can restore them if you lose every device.";
}

/** What Settings says about recovery through Rhema support. */
export function protectionDetail(s: BackupStatus): string {
  if (s.advancedProtection)
    return "On. Only your devices and your backup PIN can restore your history. Rhema support cannot help.";
  return "Off. If you lose every device and forget your backup PIN, Rhema support can help you get your history back.";
}

/** The sheet before turning on advanced protection. */
export const PROTECTION_ASK = {
  title: "Turn on advanced protection?",
  detail:
    "Rhema keeps an encrypted copy of your backup key that only Rhema support can open, to help you if you lose every device and forget your backup PIN. Turning this on deletes that copy. Your messages and your backup stay as they are. After this, Rhema can never help you recover your message history, so keep your backup PIN safe.",
  action: "Turn on",
};

/** Settings, Chats: the message backup's state, setting or changing the PIN, and advanced protection. */
export function MessageBackupSettings() {
  const client = useClient();
  const { confirm, notify } = useFeedback();
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState<Mode | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    client
      .backupStatus()
      .then((s) => {
        setStatus(s);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, [client]);
  useEffect(load, [load]);
  useEffect(() => client.onBackupEvent(() => load()), [client, load]);

  const action: { mode: Mode; label: string } | null = !status
    ? null
    : status.recoveryCodeUntil
      ? { mode: "code", label: "Enter recovery code" }
      : status.waitingForKey
        ? status.pin
          ? { mode: "recover", label: "Enter backup PIN" }
          : null
        : { mode: status.pin ? "change" : "set", label: status.pin ? "Change backup PIN" : "Set backup PIN" };

  const setProtection = async (on: boolean) => {
    if (on && !(await confirm(PROTECTION_ASK))) return;
    setSaving(true);
    try {
      await client.setAdvancedProtection(on);
      notify(
        on
          ? "Advanced protection is on. Rhema support can no longer recover your history."
          : "Advanced protection is off. Rhema support can help recover your history.",
      );
    } catch {
      notify("Rhema could not change advanced protection. Try again.");
    }
    setSaving(false);
    load();
  };

  return (
    <section aria-labelledby="message-backup-title">
      <Card className="px-4 py-3.5">
        <h3 id="message-backup-title" className="m-0 text-[15px] font-bold">
          Message backup
        </h3>
        <p className="m-0 mt-1.5 text-[13px] leading-normal text-ink-secondary">
          {status
            ? statusLine(status)
            : failed
              ? "Rhema could not check your backup."
              : "Checking your backup."}
        </p>
        {status?.lockedUntil && (
          <p className="m-0 mt-1.5 text-[13px] leading-normal text-ink-secondary">
            After too many wrong PINs, you can try again at {tryAgainAt(status.lockedUntil)}.
          </p>
        )}
        {status?.recoveryCodeUntil && (
          <p className="m-0 mt-1.5 text-[13px] leading-normal text-ink-secondary">
            Rhema support started a recovery of your message history. The recovery code works until{" "}
            {tryAgainAt(status.recoveryCodeUntil)}.
          </p>
        )}
        {action && (
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setMode(action.mode)}>
            {action.label}
          </Button>
        )}
        {status && (
          <SettingRow
            className="-mx-4 mt-2 border-t border-line pb-0"
            title="Advanced protection"
            detail={
              !status.advancedProtection && !status.pin
                ? `${protectionDetail(status)} Set a backup PIN before you turn this on.`
                : protectionDetail(status)
            }
            trailing={
              <Switch
                label="Advanced protection"
                checked={status.advancedProtection}
                disabled={saving || (!status.advancedProtection && !status.pin)}
                onChange={(on) => void setProtection(on)}
              />
            }
          />
        )}
      </Card>
      <BackupSheets mode={mode} setMode={setMode} onDone={() => load()} />
    </section>
  );
}

/** How long a new device waits for another of its devices to hand over the key before asking. */
export const RECOVER_AFTER_MS = 8_000;

/**
 * Asks once for a backup PIN after the first backup, asks for it on a device that cannot read its
 * history when no other device has handed over the key, and asks for the recovery code when Rhema
 * support has started a recovery.
 */
export function BackupPrompts({ recoverAfterMs = RECOVER_AFTER_MS }: { recoverAfterMs?: number }) {
  const client = useClient();
  const { notify } = useFeedback();
  const [mode, setMode] = useState<Mode | null>(null);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let askedForCode = false;
    const check = async (again: boolean) => {
      const s = await client.backupStatus().catch(() => null);
      if (stopped || !s) return;
      if (s.recoveryCodeUntil && !askedForCode) {
        askedForCode = true;
        setMode((m) => m ?? "code");
      } else if (s.waitingForKey && s.pin) {
        // Another device may hand the key over in a moment. Without one, ask for the PIN now.
        if (again || s.otherDevices === 0) setMode((m) => m ?? "recover");
        else timer = setTimeout(() => void check(true), recoverAfterMs);
      } else if (s.hasKey && !s.pin && !s.pinAsked) setMode((m) => m ?? "set");
    };
    timer = setTimeout(() => void check(false), 1_500);
    const off = client.onBackupEvent((e) => {
      if (e.kind === "recovery_started") notify(RECOVERY_STARTED);
      void check(false);
    });
    return () => {
      stopped = true;
      off();
      if (timer) clearTimeout(timer);
    };
  }, [client, recoverAfterMs, notify]);

  return (
    <BackupSheets
      mode={mode}
      setMode={setMode}
      onSkip={() => undefined}
      onDone={(done, ok) => {
        // Rhema asks once, however the person closes the sheet.
        if (done === "set" && !ok) void client.skipBackupPin().catch(() => undefined);
      }}
    />
  );
}
